import { Box, IconButton } from '@mui/material';
import { GridCellParams, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { getIncludedSelectionIds } from '../../utils/gridSelection';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { TMessage } from '../../@types';
import { QUERY_KEYS } from '../../constants';
import { useInvalidateQuery } from '../../hooks/useInvalidateQuery';
import { usePagination } from '../../hooks/usePagination';
import { deleteData } from '../../utils/api';
import { StyledMessagesTable } from '../atoms/StyledMessagesTable';
import DeleteIcon from '../icons/DeleteIcon';
import MessagesLayout from '../layouts/MessagesLayout';
import MessagesThread from '../organisms/MessagesThread';
import useMutateData from '../../hooks/useMutateData';
import dayjs from 'dayjs';

const MessagesSentPage = () => {
  const invalidateData = useInvalidateQuery();
  const { data: messages } = usePagination<TMessage>('messages/sent');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [messageThread, setMessageThread] = useState({
    open: false,
    messageId: '',
  });

  const { mutate: deleteMessage } = useMutation({
    mutationFn: (data: { messageIds: string[] }) => deleteData('messages', 'feed', {}, data),
    onSuccess: () => {
      invalidateData([QUERY_KEYS.sentMessages]);
    },
  });

  const { updateData } = useMutateData('messages', [QUERY_KEYS.messagesCount], 'feed', true);

  const deleteMessages = () => {
    setMessageThread({
      open: false,
      messageId: '',
    });
    deleteMessage({
      messageIds: selectedRows,
    });
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectedRows(getIncludedSelectionIds(newSelection));
  };

  const onCellClick = (params: GridCellParams) => {
    setMessageThread({
      open: true,
      messageId: params.row.id,
    });

    const selectedMessage = messages?.items.find((item) => item.id === params.row.id);

    const messageReplyIds = selectedMessage?.replies.map((replyMsg) => replyMsg.id);
    const markAsReadMessageIds = messageReplyIds ? [selectedMessage?.id, ...messageReplyIds] : [selectedMessage?.id];

    updateData('', {
      messageIds: markAsReadMessageIds,
    });
  };

  const columns: GridColDef[] = [
    { field: 'to', headerName: 'To:', width: 150 },
    {
      field: 'subject',
      headerName: 'Subject:',
      width: 150,
    },
    {
      field: 'date',
      headerName: 'Date:',
      width: 150,
    },
    {
      field: 'spacer',
      headerName: '',
      flex: 1,
      sortable: false,
    },
    {
      field: 'delete',
      headerName: '',
      width: 150,
      renderHeader: () =>
        selectedRows.length > 0 ? (
          <IconButton onClick={deleteMessages}>
            <DeleteIcon />
          </IconButton>
        ) : null,
      sortable: false,
    },
  ];

  const rows = messages?.items.map((message) => {
    return {
      to: message.receiver,
      subject: message.topic,
      date: dayjs(message.createdAt).format('DD.MM.YYYY'),
      id: message.id,
    };
  });

  return (
    <MessagesLayout>
      <Box sx={{ display: 'flex', gap: '0 24px' }}>
        {rows && rows.length > 0 ? (
          <StyledMessagesTable
            columns={columns}
            rows={rows}
            disableColumnMenu
            checkboxSelection
            disableRowSelectionExcludeModel
            disableRowSelectionOnClick
            onRowSelectionModelChange={handleSelectionChange}
            onCellClick={onCellClick}
          />
        ) : (
          'No sent messages to display'
        )}
        {messageThread.open && (
          <MessagesThread
            parentId={messageThread.messageId}
            sentTab
            closeThread={() => setMessageThread({ open: false, messageId: '' })}
          />
        )}
      </Box>
    </MessagesLayout>
  );
};

export default MessagesSentPage;
